<script>
	import { onMount } from "svelte";
	import { fade } from "svelte/transition";
	import { Dates } from "../../utils/date";
	import { getResourceUrl } from "../../utils/resource";
	import TableToolbarNoPaging from "../../components/TableToolbarNoPaging.svelte";
	import { menu, menuSub, menuSubSub } from "../../stores";
	import { ErrorCodes } from "../../define/define";
	import { postFileApi, putFileApi, delFileApi, getApi, delApi, putApi } from "../../service/api";
	import { handleFileSelect, validateFile, handleImagePreview, resetFileInput } from "../../utils/file";
	import { playAudio, stopAudio } from "../../utils/audio";
	import { downloadFile } from "../../utils/download";
	import { getImageUrl, handleImageError, ACTIVE_OPTIONS} from "../../utils/musicCommon";

	// 상수 정의
	const BASE_URL = "/sound/signal";
	const LIST_URL = "/sound/signals";

	// 상태 관리
	let currentPage = 1;
	let pageSize = 20;
	let totalPages = 1;
	let totalItems = 0;
	let soundSignals = [];
	let filteredData = [];
	let searchKeyword = "";
	let currentSort = "updated";
	let currentSortDirection = "desc";

	// 편집 관련 상태
	let showModal = false;
	let modalTitle = "";
	let isSaving = false;

	// 상세보기 모달 상태
	let showDetailModal = false;
	let detailItem = {};

	// 오디오 상태
	let audioPlayer = null;
	let isPlaying = false;
	let currentPlayingPath = "";

	// 편집 아이템 초기값
	const defaultEditItem = {
		id: 0,
		subjectKo: "",
		subjectEn: "",
		subjectJa: "",
		subjectZhCn: "",
		subjectZhTw: "",
		image: "",
		pathInhaling: "",
		pathExhaling: "",
		isActive: false,
		createdAt: null,
		updatedAt: null,
	};

	let editItem = { ...defaultEditItem };

	// 선택된 파일 관리
	let selectedFiles = {
		image: null,
		inhaling: null,
		exhaling: null,
	};

	// 파일 미리보기 URL
	let filePreviewUrls = {
		image: "",
		inhaling: "",
		exhaling: "",
	};

	// 데이터 로드
	const loadData = async () => {
		try {
			const result = await getApi(`${LIST_URL}/${pageSize}/${currentPage}`);
			if (result.resultCode === ErrorCodes.OK) {
				soundSignals = result.data?.list || [];
				totalItems = result.data?.total || 0;
				totalPages = result.data?.totalPage || 1;
				filteredData = getFilteredData(soundSignals);
			}
		} catch (error) {
			console.error("데이터 로드 실패:", error);
			alert("데이터 로드에 실패했습니다.");
		}
	};

	// 상세 정보 로드
	const loadDetailItem = async (id) => {
		try {
			const result = await getApi(`${BASE_URL}/${id}`);
			if (result.resultCode === ErrorCodes.OK) {
				return result.data;
			}
		} catch (error) {
			console.error("상세 정보 로드 실패:", error);
		}
		return null;
	};

	// 검색 및 필터링
	const getFilteredData = (data) => {
		let filtered = [...data];

		// 검색 필터링
		if (searchKeyword) {
			const keyword = searchKeyword.toLowerCase();
			filtered = filtered.filter((item) => {
				return (
					item.subjectKo?.toLowerCase().includes(keyword) ||
					item.subjectEn?.toLowerCase().includes(keyword) ||
					item.subjectJa?.toLowerCase().includes(keyword) ||
					item.id.toString().includes(keyword)
				);
			});
		}

		// 정렬 적용
		return sortData(filtered);
	};

	// 정렬 함수
	const sortData = (list) => {
		const sortedList = [...list];
		const direction = currentSortDirection === "asc" ? 1 : -1;

		switch (currentSort) {
			case "updated":
				sortedList.sort((a, b) => direction * (new Date(b.updatedAt) - new Date(a.updatedAt)));
				break;
			case "created":
				sortedList.sort((a, b) => direction * (new Date(b.createdAt) - new Date(a.createdAt)));
				break;
			case "id":
				sortedList.sort((a, b) => direction * (a.id - b.id));
				break;
			case "subject":
				sortedList.sort((a, b) => direction * (a.subjectKo || "").localeCompare(b.subjectKo || ""));
				break;
		}

		return sortedList;
	};

	// 검색 실행
	const fnSearch = () => {
		currentPage = 1;
		loadData();
	};

	// 새로고침
	const fnReload = () => {
		searchKeyword = "";
		currentSort = "updated";
		currentSortDirection = "desc";
		currentPage = 1;
		loadData();
	};

	// 입력 핸들러
	const handleInput = (field, value) => {
		if (editItem[field] !== value) {
			editItem[field] = value;
		}
	};

	// 파일 선택 핸들러
	const handleSoundFileSelect = (event, type) => {
		const file = event.target.files[0];
		if (!file) return;

		let fileType = "";
		switch (type) {
			case "image":
				fileType = "image";
				break;
			case "inhaling":
			case "exhaling":
				fileType = "audio";
				break;
		}

		if (!validateFile(file, fileType)) return;

		selectedFiles[type] = file;
		editItem[type === "image" ? "image" : type === "inhaling" ? "pathInhaling" : "pathExhaling"] = file.name;

		// 파일 미리보기 URL 생성
		const url = URL.createObjectURL(file);
		filePreviewUrls[type] = url;
	};

	// 파일 선택 취소
	const cancelFileSelection = (type) => {
		selectedFiles[type] = null;
		editItem[type === "image" ? "image" : type === "inhaling" ? "pathInhaling" : "pathExhaling"] = "";
		if (filePreviewUrls[type]) {
			URL.revokeObjectURL(filePreviewUrls[type]);
			filePreviewUrls[type] = "";
		}
		// 파일 input 초기화
		const input = document.getElementById(`${type}File`);
		if (input) input.value = "";
	};

	// 오디오 재생/정지
	const toggleAudio = (audioPath, type) => {
		const fullPath = audioPath.startsWith("http") ? audioPath : getResourceUrl(audioPath);

		if (isPlaying && currentPlayingPath === fullPath) {
			stopAudio(
				audioPlayer,
				(v) => (audioPlayer = v),
				(v) => (isPlaying = v)
			);
			currentPlayingPath = "";
		} else {
			if (audioPlayer) {
				stopAudio(
					audioPlayer,
					(v) => (audioPlayer = v),
					(v) => (isPlaying = v)
				);
			}

			let audioToPlay;
			if (selectedFiles[type]) {
				audioToPlay = URL.createObjectURL(selectedFiles[type]);
			} else {
				audioToPlay = fullPath;
			}

			playAudio(
				audioToPlay,
				{},
				(v) => (audioPlayer = v),
				(v) => (isPlaying = v),
				(path) => path
			);
			currentPlayingPath = fullPath;
		}
	};

	// 유효성 검사
	const validateItem = () => {
		if (!editItem.subjectKo) {
			alert("한국어 제목을 입력하세요.");
			return false;
		}

		// 신규 등록시에는 최소 하나의 사운드 파일이 필요
		if (!editItem.id) {
			if (!selectedFiles.inhaling && !selectedFiles.exhaling) {
				alert("들숨 사운드 또는 날숨 사운드 중 하나는 등록해야 합니다.");
				return false;
			}
		}

		return true;
	};

	// 저장 핸들러
	const handleSave = async () => {
		if (!validateItem()) return;

		isSaving = true;
		try {
			const formData = new FormData();
			formData.append("subjectKo", editItem.subjectKo);
			formData.append("subjectEn", editItem.subjectEn || "");
			formData.append("subjectJa", editItem.subjectJa || "");
			formData.append("subjectZhCn", editItem.subjectZhCn || "");
			formData.append("subjectZhTw", editItem.subjectZhTw || "");
			formData.append("isActive", editItem.isActive || "false");

			if (selectedFiles.image) {
				formData.append("image", selectedFiles.image);
			}
			if (selectedFiles.inhaling) {
				formData.append("pathInhaling", selectedFiles.inhaling);
			}
			if (selectedFiles.exhaling) {
				formData.append("pathExhaling", selectedFiles.exhaling);
			}

			let result;
			if (editItem.id) {
				formData.append("id", editItem.id);
				result = await putFileApi(BASE_URL, formData);
			} else {
				result = await postFileApi(BASE_URL, formData);
			}

			if (result.resultCode === ErrorCodes.OK) {
				alert(editItem.id ? "명상 - 사운드 신호 수정이 완료되었습니다." : "명상 - 사운드 신호 등록이 완료되었습니다.");
				closeModal();
				await loadData();
			} else {
				alert(result.resultDescKo || "오류가 발생했습니다.");
			}
		} catch (error) {
			console.error("저장 실패:", error);
			alert("저장에 실패했습니다.");
		} finally {
			isSaving = false;
		}
	};

	// 신규 등록
	const fnProcessRegist = () => {
		editItem = { ...defaultEditItem };
		selectedFiles = { image: null, inhaling: null, exhaling: null };
		filePreviewUrls = { image: "", inhaling: "", exhaling: "" };
		modalTitle = '<i class="bx bx-plus-circle me-1"></i>명상 - 사운드 신호 신규 등록';
		showModal = true;
	};

	// 편집
	const fnProcessEdit = async (item) => {
		// 상세 정보 로드
		const detailData = await loadDetailItem(item.id);
		if (detailData) {
			editItem = { ...detailData };
		} else {
			editItem = { ...item };
		}

		selectedFiles = { image: null, inhaling: null, exhaling: null };
		filePreviewUrls = { image: "", inhaling: "", exhaling: "" };
		modalTitle = '<i class="bx bx-edit me-1"></i>명상 - 사운드 신호 편집';
		if (showDetailModal) {
			closeDetailModal();
		}
		showModal = true;
	};

	// 삭제
	const fnDelete = async (id) => {
		if (confirm("정말 삭제하시겠습니까?")) {
			try {
				const result = await delApi(`${BASE_URL}/${id}`);
				if (result.resultCode === ErrorCodes.OK) {
					alert("삭제가 완료되었습니다.");
					await loadData();
				} else {
					alert(result.resultDescKo || "삭제에 실패했습니다.");
				}
			} catch (error) {
				console.error("삭제 실패:", error);
				alert("삭제에 실패했습니다.");
			}
		}
	};

	const fnRegistServer = () => {
		if (confirm("서버에 적용하시겠습니까?")) {
			putApi(`${BASE_URL}/cache`, {}).then((success) => {
				if (success) {
					alert("서버 적용이 완료되었습니다.");
				}
			});
		}
	};

	// 상세보기
	const showDetail = async (item) => {
		const detailData = await loadDetailItem(item.id);
		if (detailData) {
			detailItem = { ...detailData };
		} else {
			detailItem = { ...item };
		}
		showDetailModal = true;
	};

	// 모달 닫기
	const closeModal = () => {
		showModal = false;
		editItem = { ...defaultEditItem };
		selectedFiles = { image: null, inhaling: null, exhaling: null };
		// URL 해제
		Object.values(filePreviewUrls).forEach((url) => {
			if (url) URL.revokeObjectURL(url);
		});
		filePreviewUrls = { image: "", inhaling: "", exhaling: "" };

		// 오디오 정지
		if (audioPlayer) {
			stopAudio(
				audioPlayer,
				(v) => (audioPlayer = v),
				(v) => (isPlaying = v)
			);
		}

		// 파일 input 초기화
		["image", "inhaling", "exhaling"].forEach((type) => {
			const input = document.getElementById(`${type}File`);
			if (input) input.value = "";
		});
	};

	// 상세 모달 닫기
	const closeDetailModal = () => {
		showDetailModal = false;
		detailItem = {};
		if (audioPlayer) {
			stopAudio(
				audioPlayer,
				(v) => (audioPlayer = v),
				(v) => (isPlaying = v)
			);
		}
	};

	// 반응형 구문
	$: if (searchKeyword !== undefined) {
		filteredData = getFilteredData(soundSignals);
	}

	$: if (currentSort || currentSortDirection) {
		filteredData = getFilteredData(soundSignals);
	}

	onMount(() => {
		menu.set("콘텐츠 관리");
		menuSub.set("음원 관리");
		menuSubSub.set("명상 - 사운드 신호");
		loadData();
	});
</script>

<div class="card">
	<div class="card-header d-flex justify-content-between align-items-center">
		<h5 class="mb-0">명상 - 사운드 신호 관리</h5>
		<div class="d-flex align-items-center">
			<input
				type="text"
				class="form-control form-control-sm me-2"
				placeholder="ID 또는 제목으로 검색"
				style="width: 250px;"
				bind:value={searchKeyword}
				on:keydown={(e) => e.key === "Enter" && fnSearch()}
			/>
			<select class="form-select form-select-sm me-2" style="width: auto;" bind:value={currentSort}>
				<option value="updated">수정일시순</option>
				<option value="created">등록일시순</option>
				<option value="id">ID순</option>
				<option value="subject">제목순</option>
			</select>
			<div class="btn-group me-2">
				<button
					type="button"
					class="btn btn-sm {currentSortDirection === 'asc' ? 'btn-primary' : 'btn-outline-primary'}"
					title="오름차순"
					on:click={() => (currentSortDirection = "asc")}
				>
					<i class="bx bx-sort-up"></i>
				</button>
				<button
					type="button"
					class="btn btn-sm {currentSortDirection === 'desc' ? 'btn-primary' : 'btn-outline-primary'}"
					title="내림차순"
					on:click={() => (currentSortDirection = "desc")}
				>
					<i class="bx bx-sort-down"></i>
				</button>
			</div>
			<TableToolbarNoPaging onReload={fnReload} onRegist={fnProcessRegist} onRegistServer={fnRegistServer}/>
		</div>
	</div>

	<div class="card-body">
		{#if filteredData.length === 0}
			<div class="text-center p-5">
				<i class="bx bx-info-circle fs-1 text-muted"></i>
				<p class="text-muted mt-3">데이터가 없습니다.</p>
			</div>
		{:else}
			<div class="table-responsive">
				<table class="table table-hover">
					<thead>
						<tr class="text-center">
							<th style="width: 80px;">ID</th>
							<th style="width: 100px;">이미지</th>
							<th style="width: *">제목 (한국어)</th>
							<th style="width: 150px;">제목 (영어)</th>
							<th style="width: 110px;">들숨 사운드</th>
							<th style="width: 110px;">날숨 사운드</th>
							<th style="width: 100px;">사용여부</th>
							<th style="width: 150px;">등록일시</th>
							<th style="width: 150px;">수정일시</th>
							<th style="width: 150px;">작업</th>
						</tr>
					</thead>
					<tbody>
						{#each filteredData as item (item.id)}
							<tr class="text-center">
								<td>{item.id}</td>
								<td>
									{#if item.image}
										<img
											src={item.image}
											alt={item.subjectKo}
											class="rounded"
											style="width: 50px; height: 50px; object-fit: cover;"
											on:error={handleImageError}
										/>
									{:else}
										<div
											class="bg-light d-flex align-items-center justify-content-center rounded"
											style="width: 100px; height: 50px;"
										>
											<i class="bx bx-image text-muted"></i>
										</div>
									{/if}
								</td>
								<td>
									<div class="fw-semibold">{item.subjectKo || "-"}</div>
								</td>
								<td>
									<div>{item.subjectEn || "-"}</div>
								</td>
								<td>
									{#if item.pathInhaling}
										<div class="d-flex align-items-center">
											<button
												class="btn btn-sm btn-outline-primary me-1"
												title="재생"
												on:click={() => toggleAudio(item.pathInhaling, "inhaling")}
											>
												<i
													class="bx {isPlaying && currentPlayingPath === item.pathInhaling ? 'bx-stop' : 'bx-play'}"
													style="font-size: 0.8rem;"
												></i>
											</button>
											<button
												class="btn btn-sm btn-outline-secondary"
												title="다운로드"
												on:click={() => downloadFile(item.pathInhaling, (path) => path)}
											>
												<i class="bx bx-download" style="font-size: 0.8rem;"></i>
											</button>
										</div>
									{:else}
										<span class="text-muted">-</span>
									{/if}
								</td>
								<td>
									{#if item.pathExhaling}
										<div class="d-flex align-items-center">
											<button
												class="btn btn-sm btn-outline-primary me-1"
												title="재생"
												on:click={() => toggleAudio(item.pathExhaling, "exhaling")}
											>
												<i
													class="bx {isPlaying && currentPlayingPath === item.pathExhaling ? 'bx-stop' : 'bx-play'}"
													style="font-size: 0.8rem;"
												></i>
											</button>
											<button
												class="btn btn-sm btn-outline-secondary"
												title="다운로드"
												on:click={() => downloadFile(item.pathExhaling, (path) => path)}
											>
												<i class="bx bx-download" style="font-size: 0.8rem;"></i>
											</button>
										</div>
									{:else}
										<span class="text-muted">-</span>
									{/if}
								</td>
								<td>
									{#if item.isActive}<b>사용</b>{:else}미사용{/if}
								</td>
								<td>
									<small>{Dates.defaultConvertFull(item.createdAt)}</small>
								</td>
								<td>
									<small>{Dates.defaultConvertFull(item.updatedAt)}</small>
								</td>
								<td>
									<div class="btn-group">
										<button class="btn btn-sm btn-outline-primary" on:click={() => showDetail(item)}>상세</button>
										<button class="btn btn-sm btn-primary" on:click={() => fnProcessEdit(item)}>편집</button>
										<button class="btn btn-sm btn-outline-danger" on:click={() => fnDelete(item.id)}>삭제</button>
									</div>
								</td>
							</tr>
						{/each}
					</tbody>
				</table>
			</div>
		{/if}
	</div>
</div>

<!-- 등록/편집 모달 -->
{#if showModal}
	<div class="modal fade show" style="display: block;" tabindex="-1" transition:fade={{ duration: 200 }}>
		<div class="modal-dialog modal-dialog-centered modal-xl">
			<div class="modal-content">
				<div class="modal-header">
					<h5 class="modal-title">{@html modalTitle}</h5>
					<button type="button" class="btn-close" on:click={closeModal}></button>
				</div>
				<div class="modal-body">
					<!-- 제목 정보 -->
					<div class="mb-4">
						<h6 class="border-bottom pb-2 mb-3">제목 정보</h6>
						<div class="row">
							<div class="col-md-6">
								<label for="subjectKo" class="form-label">
									한국어 제목 <span class="text-danger">*</span>
								</label>
								<input
									type="text"
									class="form-control form-control-sm"
									id="subjectKo"
									bind:value={editItem.subjectKo}
									placeholder="한국어 제목을 입력하세요"
								/>
							</div>
							<div class="col-md-6">
								<label for="subjectEn" class="form-label">영어 제목</label>
								<input
									type="text"
									class="form-control form-control-sm"
									id="subjectEn"
									bind:value={editItem.subjectEn}
									placeholder="영어 제목을 입력하세요"
								/>
							</div>
						</div>
						<div class="row mt-3">
							<div class="col-md-4">
								<label for="subjectJa" class="form-label">일본어 제목</label>
								<input
									type="text"
									class="form-control form-control-sm"
									id="subjectJa"
									bind:value={editItem.subjectJa}
									placeholder="일본어 제목을 입력하세요"
								/>
							</div>
							<div class="col-md-4">
								<label for="subjectZhCn" class="form-label">중국어 간체 제목</label>
								<input
									type="text"
									class="form-control form-control-sm"
									id="subjectZhCn"
									bind:value={editItem.subjectZhCn}
									placeholder="중국어 간체 제목을 입력하세요"
								/>
							</div>
							<div class="col-md-4">
								<label for="subjectZhTw" class="form-label">중국어 번체 제목</label>
								<input
									type="text"
									class="form-control form-control-sm"
									id="subjectZhTw"
									bind:value={editItem.subjectZhTw}
									placeholder="중국어 번체 제목을 입력하세요"
								/>
							</div>
						</div>
					</div>

					<!-- 이미지 파일 -->
					<div class="mb-4">
						<h6 class="border-bottom pb-2 mb-3">이미지</h6>
						<div class="row">
							<div class="col-md-6">
								<label for="image" class="form-label">이미지 파일</label>
								<div class="input-group input-group-sm">
									<input
										type="text"
										class="form-control form-control-sm"
										id="image"
										value={editItem.image}
										readonly
										placeholder="이미지 파일을 선택하세요"
									/>
									<label class="input-group-text" for="imageFile" style="cursor: pointer;">
										<i class="bx bx-upload"></i>
									</label>
									<input
										type="file"
										id="imageFile"
										accept="image/*"
										class="d-none"
										on:change={(e) => handleSoundFileSelect(e, "image")}
									/>
									{#if selectedFiles.image}
										<button
											type="button"
											class="btn btn-sm btn-outline-danger"
											title="선택 취소"
											on:click={() => cancelFileSelection("image")}
										>
											<i class="bx bx-x"></i>
										</button>
									{/if}
								</div>
							</div>
							<div class="col-md-6">
								{#if filePreviewUrls.image || editItem.image}
									<div class="mt-2">
										<img
											src={filePreviewUrls.image || editItem.image}
											alt="미리보기"
											class="img-thumbnail"
											style="max-width: 200px; max-height: 150px;"
											on:error={handleImageError}
										/>
									</div>
								{/if}
							</div>
						</div>
					</div>

					<!-- 사운드 파일 -->
					<div class="mb-4">
						<h6 class="border-bottom pb-2 mb-3">사운드 파일</h6>

						<!-- 들숨 사운드 -->
						<div class="row mb-3">
							<div class="col-md-6">
								<label for="pathInhaling" class="form-label">들숨 사운드</label>
								<div class="input-group input-group-sm">
									<input
										type="text"
										class="form-control form-control-sm"
										id="pathInhaling"
										value={editItem.pathInhaling}
										readonly
										placeholder="들숨 사운드 파일을 선택하세요"
									/>
									<label class="input-group-text" for="inhalingFile" style="cursor: pointer;">
										<i class="bx bx-upload"></i>
									</label>
									<input
										type="file"
										id="inhalingFile"
										accept="audio/*,.aac,.m4a,.mp3,.wav,.ogg,.flac"
										class="d-none"
										on:change={(e) => handleSoundFileSelect(e, "inhaling")}
									/>
									{#if editItem.pathInhaling}
										<button
											type="button"
											class="btn btn-sm btn-outline-primary"
											on:click={() => toggleAudio(editItem.pathInhaling, "inhaling")}
										>
											<i class="bx {isPlaying && currentPlayingPath === editItem.pathInhaling ? 'bx-stop' : 'bx-play'}"></i>
										</button>
									{/if}
									{#if selectedFiles.inhaling}
										<button
											type="button"
											class="btn btn-sm btn-outline-danger"
											title="선택 취소"
											on:click={() => cancelFileSelection("inhaling")}
										>
											<i class="bx bx-x"></i>
										</button>
									{/if}
								</div>
							</div>

							<!-- 날숨 사운드 -->
							<div class="col-md-6">
								<label for="pathExhaling" class="form-label">날숨 사운드</label>
								<div class="input-group input-group-sm">
									<input
										type="text"
										class="form-control form-control-sm"
										id="pathExhaling"
										value={editItem.pathExhaling}
										readonly
										placeholder="날숨 사운드 파일을 선택하세요"
									/>
									<label class="input-group-text" for="exhalingFile" style="cursor: pointer;">
										<i class="bx bx-upload"></i>
									</label>
									<input
										type="file"
										id="exhalingFile"
										accept="audio/*,.aac,.m4a,.mp3,.wav,.ogg,.flac"
										class="d-none"
										on:change={(e) => handleSoundFileSelect(e, "exhaling")}
									/>
									{#if editItem.pathExhaling}
										<button
											type="button"
											class="btn btn-sm btn-outline-primary"
											on:click={() => toggleAudio(editItem.pathExhaling, "exhaling")}
										>
											<i class="bx {isPlaying && currentPlayingPath === editItem.pathExhaling ? 'bx-stop' : 'bx-play'}"></i>
										</button>
									{/if}
									{#if selectedFiles.exhaling}
										<button
											type="button"
											class="btn btn-sm btn-outline-danger"
											title="선택 취소"
											on:click={() => cancelFileSelection("exhaling")}
										>
											<i class="bx bx-x"></i>
										</button>
									{/if}
								</div>
							</div>
						</div>
					</div>

					<!-- 사용 여부 -->
					<div class="mb-4">
						<h6 class="border-bottom pb-2 mb-3">사용 여부</h6>
						<div class="row mb-3">
							<div class="col-md-6">
								<div class="col-md-3">
									<div class="mb-3">
										<select
											class="form-select form-select-sm"
											id="isActive"
											bind:value={editItem.isActive}
											on:change={(e) => handleInput("isActive", e.target.value === "true")}
										>											
										<option value={true}>사용</option>
										<option value={false}>미사용</option>											
										</select>
									</div>
								</div>
							</div>
						</div>
					</div>

					{#if editItem.id}
						<!-- 시스템 정보 -->
						<div class="mb-4">
							<h6 class="border-bottom pb-2 mb-3">시스템 정보</h6>
							<div class="row">
								<div class="col-md-6">
									<small class="text-muted">생성일시: {Dates.defaultConvertFull(editItem.createdAt)}</small>
								</div>
								<div class="col-md-6">
									<small class="text-muted">수정일시: {Dates.defaultConvertFull(editItem.updatedAt)}</small>
								</div>
							</div>
						</div>
					{/if}
				</div>
				<div class="modal-footer">
					<button type="button" class="btn btn-sm btn-outline-secondary" on:click={closeModal} disabled={isSaving}>취소</button>
					<button type="button" class="btn btn-sm btn-primary" on:click={handleSave} disabled={isSaving}>
						{#if isSaving}
							<span class="spinner-border spinner-border-sm me-1"></span>
							처리 중...
						{:else}
							{editItem.id ? "수정" : "등록"}
						{/if}
					</button>
				</div>
			</div>
		</div>
	</div>
	<div class="modal-backdrop fade show" transition:fade={{ duration: 200 }}></div>
{/if}

<!-- 상세보기 모달 -->
{#if showDetailModal}
	<div class="modal fade show" style="display: block;" tabindex="-1" transition:fade={{ duration: 200 }}>
		<div class="modal-dialog modal-dialog-centered modal-lg">
			<div class="modal-content">
				<div class="modal-header">
					<h5 class="modal-title">
						<i class="bx bx-sound me-1"></i>
						명상 - 사운드 신호 상세 정보
					</h5>
					<button type="button" class="btn-close" on:click={closeDetailModal}></button>
				</div>
				<div class="modal-body">
					<div class="row">
						<div class="col-md-6">
							<h6 class="border-bottom pb-2 mb-3">기본 정보</h6>
							<div class="row mb-2">
								<div class="col-4 fw-bold">ID</div>
								<div class="col-8">{detailItem.id}</div>
							</div>
							<div class="row mb-2">
								<div class="col-4 fw-bold">사용 여부</div>
								<div class="col-8">{detailItem.isActive ? "사용" : "미사용"}</div>
							</div>
						</div>
						<div class="col-md-6">
							<h6 class="border-bottom pb-2 mb-3">시스템 정보</h6>
							<div class="row mb-2">
								<div class="col-4 fw-bold">생성일시</div>
								<div class="col-8">
									<small>{Dates.defaultConvertFull(detailItem.createdAt)}</small>
								</div>
							</div>
							<div class="row mb-2">
								<div class="col-4 fw-bold">수정일시</div>
								<div class="col-8">
									<small>{Dates.defaultConvertFull(detailItem.updatedAt)}</small>
								</div>
							</div>
						</div>
					</div>

					<!-- 제목 정보 -->
					<div class="row mt-4">
						<div class="col-12">
							<h6 class="border-bottom pb-2 mb-3">제목 정보</h6>
							<div class="row">
								<div class="col-md-6">
									<div class="mb-2">
										<strong>한국어:</strong>
										{detailItem.subjectKo || "-"}
									</div>
									<div class="mb-2">
										<strong>영어:</strong>
										{detailItem.subjectEn || "-"}
									</div>
									<div class="mb-2">
										<strong>일본어:</strong>
										{detailItem.subjectJa || "-"}
									</div>
								</div>
								<div class="col-md-6">
									<div class="mb-2">
										<strong>중국어 간체:</strong>
										{detailItem.subjectZhCn || "-"}
									</div>
									<div class="mb-2">
										<strong>중국어 번체:</strong>
										{detailItem.subjectZhTw || "-"}
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- 이미지 -->
					{#if detailItem.image}
						<div class="row mt-4">
							<div class="col-12">
								<h6 class="border-bottom pb-2 mb-3">이미지</h6>
								<div class="text-center">
									<img
										src={detailItem.image}
										alt={detailItem.subjectKo}
										class="img-thumbnail"
										style="max-width: 300px; max-height: 200px;"
										on:error={handleImageError}
									/>
								</div>
							</div>
						</div>
					{/if}

					<div class="row mt-4">
						<div class="col-12">
							<h6 class="border-bottom pb-2 mb-3">사운드 파일</h6>
							<div class="row">
								<div class="col-md-6">
									<div class="card">
										<div class="card-header">
											<i class="bx bx-import me-1"></i>
											들숨 사운드
										</div>
										<div class="card-body">
											{#if detailItem.pathInhaling}
												<div class="d-flex align-items-center justify-content-between">
													<small class="text-muted text-truncate me-2">
														{detailItem.pathInhaling}
													</small>
													<div class="btn-group">
														<button
															class="btn btn-sm btn-primary"
															on:click={() => toggleAudio(detailItem.pathInhaling, "inhaling")}
														>
															<i
																class="bx {isPlaying && currentPlayingPath === detailItem.pathInhaling
																	? 'bx-stop'
																	: 'bx-play'}"
															></i>
														</button>
														<button
															class="btn btn-sm btn-outline-secondary"
															on:click={() => downloadFile(detailItem.pathInhaling, (path) => path)}
														>
															<i class="bx bx-download"></i>
														</button>
													</div>
												</div>
											{:else}
												<span class="text-muted">파일 없음</span>
											{/if}
										</div>
									</div>
								</div>
								<div class="col-md-6">
									<div class="card">
										<div class="card-header">
											<i class="bx bx-export me-1"></i>
											날숨 사운드
										</div>
										<div class="card-body">
											{#if detailItem.pathExhaling}
												<div class="d-flex align-items-center justify-content-between">
													<small class="text-muted text-truncate me-2">
														{detailItem.pathExhaling}
													</small>
													<div class="btn-group">
														<button
															class="btn btn-sm btn-primary"
															on:click={() => toggleAudio(detailItem.pathExhaling, "exhaling")}
														>
															<i
																class="bx {isPlaying && currentPlayingPath === detailItem.pathExhaling
																	? 'bx-stop'
																	: 'bx-play'}"
															></i>
														</button>
														<button
															class="btn btn-sm btn-outline-secondary"
															on:click={() => downloadFile(detailItem.pathExhaling, (path) => path)}
														>
															<i class="bx bx-download"></i>
														</button>
													</div>
												</div>
											{:else}
												<span class="text-muted">파일 없음</span>
											{/if}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="modal-footer">
					<button type="button" class="btn btn-sm btn-primary" on:click={() => fnProcessEdit(detailItem)}>편집</button>
					<button type="button" class="btn btn-sm btn-outline-secondary" on:click={closeDetailModal}>닫기</button>
				</div>
			</div>
		</div>
	</div>
	<div class="modal-backdrop fade show" transition:fade={{ duration: 200 }}></div>
{/if}

<style>
	.text-truncate {
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
		max-width: 200px;
	}

	/* 테이블 내 버튼들을 더 작게 스타일링 */
	:global(.table .btn-sm) {
		padding: 0.25rem 0.4rem !important;
		font-size: 0.7rem !important;
		line-height: 1.2 !important;
		min-height: auto !important;
	}

	/* 버튼 그룹 내 버튼들 간격 조정 */
	:global(.table .btn-group .btn-sm) {
		padding: 0.2rem 0.35rem !important;
		font-size: 0.65rem !important;
		line-height: 1.1 !important;
	}

	/* 아이콘 크기도 조정 */
	:global(.table .btn-sm .bx) {
		font-size: 0.8rem !important;
	}
</style>
